import MirrorBackButton from "@/components/MirrorBackButton";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import MobileLayout from "@/components/MobileLayout";
import PreviewNotice from "@/components/PreviewNotice";
import { Plus, ArrowRight, CheckCircle2, Star, ChevronLeft } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/i18n/LanguageProvider";

// ── Types ──────────────────────────────────────────────────────────────────

interface PastEntry {
  id: string;
  date: string;
  items: [string, string, string];
}

// ── Static data ────────────────────────────────────────────────────────────

const PAST_ENTRIES: PastEntry[] = [
  {
    id: "1",
    date: "Mar 23, 2026",
    items: [
      "A long conversation with my sister that reminded me what really matters.",
      "The quiet hour after everyone was asleep — just me and my thoughts.",
      "Finishing the week without feeling completely lost.",
    ],
  },
  {
    id: "2",
    date: "Mar 22, 2026",
    items: [
      "Getting the project proposal done before the deadline.",
      "The barista who remembered my order without me saying anything.",
      "A playlist that perfectly matched my walk home.",
    ],
  },
  {
    id: "3",
    date: "Mar 21, 2026",
    items: [
      "My body showing up despite being tired.",
      "A kind message from someone I hadn't heard from in months.",
      "Making it through a hard day and feeling okay at the end.",
    ],
  },
];

const PLACEHOLDER_KEYS = [
  "gratitude.placeholder1",
  "gratitude.placeholder2",
  "gratitude.placeholder3",
];

// ── Page ───────────────────────────────────────────────────────────────────

const Gratitude: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useLanguage();

  const [doneTodayItems, setDoneTodayItems] = useState<[string, string, string] | null>(null);
  const [fields, setFields] = useState<[string, string, string]>(["", "", ""]);

  const allFilled = fields.every((f) => f.trim().length > 0);

  const handleSave = () => {
    if (!allFilled) return;
    setDoneTodayItems([...fields]);
    toast({ description: t("ux.preview.session.saved"), duration: 2000 });
  };

  const setField = (i: number, val: string) => {
    setFields((prev) => {
      const next: [string, string, string] = [...prev] as [string, string, string];
      next[i] = val;
      return next;
    });
  };

  return (
    <MobileLayout className="pb-28 pt-8 relative overflow-hidden">
      <PreviewNotice kind="session" />
      {/* Ambient glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[380px] h-[220px] rounded-full opacity-[0.04]"
          style={{ background: "radial-gradient(ellipse 55% 50% at 50% 50%, hsl(187 72% 50%), transparent)" }}
        />
      </div>

      {/* BACK BUTTON */}
      <MirrorBackButton className="mb-4" />

      {/* HEADER */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex items-center justify-between mb-5"
      >
        <div>
          <p className="text-[9px] font-display tracking-[0.2em] uppercase mb-0.5"
            style={{ color: "hsl(187 72% 50% / 0.55)" }}>{t("gratitude.subtitle")}</p>
          <h1 className="font-display text-[26px] font-semibold text-foreground leading-none">{t("gratitude.title")}</h1>
        </div>
        <button
          className="w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-300"
          style={{ background: "hsl(187 72% 50% / 0.10)", border: "1px solid hsl(187 72% 50% / 0.20)" }}
        >
          <Plus size={16} style={{ color: "hsl(187 72% 50%)" }} />
        </button>
      </motion.div>

      {/* DAILY ENTRY CARD */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.06, duration: 0.5 }}
        className="rounded-2xl p-4 mb-4 relative overflow-hidden"
        style={{ background: "hsl(var(--card))", border: "1px solid hsl(187 72% 50% / 0.22)" }}
      >
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-primary/25 to-transparent" />

        <AnimatePresence mode="wait">
          {doneTodayItems ? (
            /* ── Done state ── */
            <motion.div key="done"
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="text-center py-2"
            >
              <CheckCircle2 size={28} className="mx-auto mb-2" style={{ color: "hsl(142 55% 55%)" }} />
              <p className="font-display text-[14px] font-semibold text-foreground mb-1">
                {t("gratitude.practiceComplete")}
              </p>
              <p className="text-[11px] font-body" style={{ color: "hsl(218 14% 50%)" }}>
                {t("gratitude.mirrorSeesYou")}
              </p>
            </motion.div>
          ) : (
            /* ── Entry form ── */
            <motion.div key="form"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <p className="font-display text-[14px] font-semibold text-foreground mb-4">
                {t("gratitude.whatAreYouGratefulFor")}
              </p>
              <div className="space-y-3 mb-4">
                {([0, 1, 2] as const).map((i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 font-display text-[10px] font-bold"
                      style={{ background: "hsl(187 72% 50% / 0.12)", color: "hsl(187 72% 55%)", border: "1px solid hsl(187 72% 50% / 0.22)" }}>
                      {i + 1}
                    </div>
                    <input
                      value={fields[i]}
                      onChange={(e) => setField(i, e.target.value)}
                      placeholder={t(PLACEHOLDER_KEYS[i])}
                      className="flex-1 bg-transparent text-[13px] font-body text-foreground/80 placeholder:text-foreground/25 focus:outline-none border-b pb-1.5"
                      style={{ borderColor: "hsl(187 72% 50% / 0.14)" }}
                    />
                  </div>
                ))}
              </div>
              <button onClick={handleSave}
                className="w-full py-2.5 rounded-xl font-display text-[12px] font-semibold tracking-wide transition-all duration-200 active:scale-[0.98]"
                style={{
                  background: allFilled ? "#00e5ff" : "hsl(187 72% 50% / 0.08)",
                  color: allFilled ? "#0a0a1a" : "hsl(187 72% 50% / 0.35)",
                  border: allFilled ? "none" : "1px solid hsl(187 72% 50% / 0.16)",
                }}>
                {t("ux.preview.session.keep")}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* STREAK STATS */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.14, duration: 0.5 }}
        className="rounded-2xl p-4 mb-4"
        style={{ background: "hsl(var(--card))", border: "1px solid hsl(187 72% 50% / 0.18)" }}
      >
        <div className="flex items-center gap-4">
          {/* Streak circle */}
          <div className="flex flex-col items-center shrink-0">
            <svg width="68" height="68" viewBox="0 0 68 68">
              <circle cx="34" cy="34" r="27" fill="none" stroke="hsl(218 14% 16%)" strokeWidth="5" />
              <circle cx="34" cy="34" r="27" fill="none" stroke="hsl(265 60% 55%)" strokeWidth="5"
                strokeLinecap="round" strokeDasharray={2 * Math.PI * 27} strokeDashoffset={2 * Math.PI * 27 * (1 - Math.min(7 / 30, 1))}
                transform="rotate(-90 34 34)" style={{ transition: "stroke-dashoffset 0.8s ease" }} />
              <text x="34" y="32" textAnchor="middle" fill="white" fontSize="16" fontWeight="bold" fontFamily="'Sora', sans-serif">7</text>
              <text x="34" y="44" textAnchor="middle" fill="hsl(218, 14%, 55%)" fontSize="7" fontFamily="'Sora', sans-serif">{t("progress.days")}</text>
            </svg>
          </div>
          {/* Stats grid */}
          <div className="flex-1 grid grid-cols-2 gap-2">
            {[
              { icon: "🔥", label: t("gratitude.currentStreak"), value: `7 ${t("progress.days")}` },
              { icon: "⭐", label: t("gratitude.bestStreak"),    value: `23 ${t("progress.days")}` },
            ].map((s) => (
              <div key={s.label} className="text-center rounded-xl p-2"
                style={{ background: "hsl(187 72% 50% / 0.05)", border: "1px solid hsl(187 72% 50% / 0.10)" }}>
                <p className="text-[14px] mb-0.5">{s.icon}</p>
                <p className="font-display text-[12px] font-bold text-foreground">{s.value}</p>
                <p className="text-[7px] font-body mt-0.5 leading-tight" style={{ color: "hsl(218 14% 45%)" }}>
                  {s.label}
              </p>
            </div>
          ))}
          </div>
        </div>
      </motion.div>

      {/* MIRROR REFLECTION */}
      {/* HIDDEN: fake Mirror Insight — re-wire with real Padrão B per audit 23/04 (memory #23) */}
      {false && (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.22, duration: 0.5 }}
        className="rounded-2xl p-4 mb-5 relative overflow-hidden"
        style={{ background: "linear-gradient(rgba(0,0,0,0.58), rgba(0,0,0,0.72)), url(https://lluvykhqgdnbfobjixbj.supabase.co/storage/v1/object/public/mirror-brief/room%20ex_06.jpeg) center/cover no-repeat", border: "1px solid hsl(265 60% 55% / 0.22)" }}
      >
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[hsl(265_60%_55%/0.3)] to-transparent" />
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0 mt-0.5"
            style={{ background: "hsl(265 60% 55% / 0.14)", border: "1px solid hsl(265 60% 55% / 0.25)" }}>
            <Star size={14} style={{ color: "hsl(265 70% 72%)" }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[9px] font-display tracking-[0.18em] uppercase mb-1.5"
              style={{ color: "hsl(265 70% 72% / 0.65)" }}>{t("gratitude.mirrorReflection")}</p>
            <p className="text-[12px] font-body leading-relaxed mb-3 italic"
              style={{ color: "hsl(210 20% 75%)" }}>
              {t("gratitude.mirrorReflectionText")}
            </p>
            <button onClick={() => navigate("/mirror")}
              className="flex items-center gap-1 font-display text-[11px] font-medium tracking-wide opacity-75 hover:opacity-100 transition-opacity"
              style={{ color: "hsl(265 70% 72%)" }}>
              <span>{t("gratitude.exploreWithMirror")}</span>
              <ArrowRight size={11} />
            </button>
          </div>
        </div>
      </motion.div>
      )}

      {/* PAST ENTRIES */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.30, duration: 0.5 }}
      >
        <p className="text-[11px] font-display tracking-[0.18em] uppercase mb-3"
          style={{ color: "hsl(187 72% 50% / 0.55)" }}>{t("gratitude.pastEntries")}</p>

        <div className="space-y-3">
          {PAST_ENTRIES.map((entry, i) => (
            <motion.div key={entry.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.32 + i * 0.07 }}
              className="rounded-2xl p-4"
              style={{ background: "hsl(var(--card))", border: "1px solid hsl(187 72% 50% / 0.12)" }}
            >
              <p className="text-[10px] font-display tracking-wide mb-2.5"
                style={{ color: "hsl(218 14% 50%)" }}>{entry.date}</p>
              <div className="space-y-1.5">
                {entry.items.map((item, j) => (
                  <div key={j} className="flex items-start gap-2">
                    <div className="w-4 h-4 rounded-full flex items-center justify-center shrink-0 mt-0.5 font-display text-[9px] font-bold"
                      style={{ background: "hsl(187 72% 50% / 0.10)", color: "hsl(187 72% 55%)" }}>
                      {j + 1}
                    </div>
                    <p className="text-[11px] font-body leading-snug line-clamp-1"
                      style={{ color: "hsl(210 20% 65%)" }}>{item}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

    </MobileLayout>
  );
};

export default Gratitude;
